import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import {
	ExpandLessRounded,
	ExpandMoreRounded,
	RefreshRounded,
	FilterAltRounded,
	ChevronLeftRounded,
	ChevronRightRounded,
} from "@mui/icons-material";

import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import {
	tone,
	ink,
	muted,
	line,
	panelBg,
	card,
	eyebrow,
	colHead,
	nf,
	days,
	KpiTile,
	Swatches,
	StatusMixSignal,
	DimensionToggle,
	ScopeChips,
	splitAnalyticsFilters,
} from "components/PimDashboard/DashboardComponents/shared/analyticsUi";

const STATUS_BANDS = [
	{ key: "published", label: "Published", short: "Pub", api: "PUBLISHED", color: tone.published },
	{ key: "designerPending", label: "Designer", short: "Des", api: "DESIGNER_PENDING", color: tone.designerPending },
	{ key: "sourcingPending", label: "Sourcing", short: "Src", api: "SOURCING_PENDING", color: tone.sourcingPending },
	{ key: "mrpPending", label: "MRP", short: "MRP", api: "MRP_PENDING", color: tone.mrpPending },
	{ key: "rejected", label: "Rejected", short: "Rej", api: "REJECTED", color: tone.rejected },
];

/** Blend pastel onto white for heat intensity (t in 0..1). */
const heatFill = (hex, t) => {
	const clamp = Math.max(0, Math.min(1, t));
	const raw = String(hex || "#E9ECEF").replace("#", "");
	const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
	const n = parseInt(full, 16);
	if (!Number.isFinite(n)) return "#F4F6F8";
	const r = (n >> 16) & 255;
	const g = (n >> 8) & 255;
	const b = n & 255;
	const mix = (c) => Math.round(255 + (c - 255) * (0.18 + 0.82 * clamp));
	return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

const DIMENSIONS = [
	{ key: "brand", label: "Brand" },
	{ key: "season", label: "Season" },
];

/** Fixed mid-row cards — body scrolls so Brand/Season/Category never stretch the page. */
const COMPARE_H = { xs: "auto", md: 360 };

const EMPTY_FILTER = Object.freeze({});

/** Compact multi-select. Empty = All. Optional in-menu search for long lists (e.g. Category). */
const ScopeSelect = ({ label, options, value, onChange, disabled, searchable = false }) => {
	const [query, setQuery] = useState("");
	const filtered = useMemo(() => {
		if (!searchable || !query.trim()) return options;
		const q = query.trim().toLowerCase();
		return options.filter((opt) => String(opt).toLowerCase().includes(q));
	}, [options, query, searchable]);

	return (
		<Select
			multiple
			displayEmpty
			size="small"
			disabled={disabled || !options.length}
			value={value}
			onChange={(e) => onChange(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
			onClose={() => setQuery("")}
			renderValue={(selected) =>
				!selected.length ? label : selected.length === 1 ? selected[0] : `${label}: ${selected.length}`
			}
			MenuProps={{
				autoFocus: false,
				PaperProps: { style: { maxHeight: 320 } },
				MenuListProps: { autoFocusItem: false },
			}}
			sx={{
				minWidth: 78,
				maxWidth: 104,
				fontSize: 11,
				background: "#fff",
				"& .MuiOutlinedInput-notchedOutline": { borderColor: line },
				"& .MuiSelect-select": { py: 0.35, color: value.length ? ink : muted },
			}}>
			{searchable ? (
				<ListSubheader sx={{ bgcolor: "#fff", lineHeight: 1, py: 1, px: 1.5, position: "sticky", top: 0, zIndex: 1 }}>
					<TextField
						size="small"
						autoFocus
						placeholder={`Search ${label.toLowerCase()}…`}
						fullWidth
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={(e) => e.stopPropagation()}
						onClick={(e) => e.stopPropagation()}
						inputProps={{ style: { fontSize: 12 } }}
					/>
				</ListSubheader>
			) : null}
			{filtered.length ? (
				filtered.map((opt) => (
					<MenuItem key={opt} value={opt} dense>
						<Checkbox size="small" checked={value.indexOf(opt) > -1} sx={{ py: 0.25 }} />
						<ListItemText primaryTypographyProps={{ fontSize: 11.5 }} primary={opt} />
					</MenuItem>
				))
			) : (
				<MenuItem disabled dense>
					<ListItemText primaryTypographyProps={{ fontSize: 11.5, color: muted }} primary="No matches" />
				</MenuItem>
			)}
		</Select>
	);
};

/** Compact comparison row — status as a fixed-width signal mosaic, not a full-bleed bar. */
const CompareRow = ({ row }) => {
	const total = row.total || 1;
	const segments = STATUS_BANDS.map((band) => ({
		key: band.key,
		label: band.label,
		color: band.color,
		value: row[band.key] || 0,
	}));
	return (
		<Stack direction="row" alignItems="center" spacing={0.75} sx={{ py: 0.85, borderTop: `1px solid ${line}` }}>
			<Typography sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: ink }} noWrap title={row.key}>
				{row.key}
			</Typography>
			<Typography sx={{ width: 36, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>{nf(row.total)}</Typography>
			<StatusMixSignal segments={segments} total={total} width={72} />
			<Typography sx={{ width: 36, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
				{row.publishedRate}%
			</Typography>
			<Typography sx={{ width: 36, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>
				{days(row.medianDays)}
			</Typography>
			<Typography
				sx={{
					width: 36,
					fontSize: 11.5,
					textAlign: "right",
					flexShrink: 0,
					color: row.aged30 ? "#B4696B" : muted,
				}}>
				{nf(row.aged30)}
			</Typography>
		</Stack>
	);
};

const CategoryRow = ({ row, onDrill }) => {
	const canDrill = typeof onDrill === "function" && (row.children?.length || 0) > 0;
	return (
		<Stack
			direction="row"
			alignItems="center"
			spacing={0.5}
			onClick={canDrill ? () => onDrill(row) : undefined}
			sx={{
				py: 0.75,
				borderTop: `1px solid ${line}`,
				cursor: canDrill ? "pointer" : "default",
				"&:hover": canDrill ? { background: "#F7F8FA" } : undefined,
			}}>
			<Typography sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: ink }} noWrap title={row.key}>
				{row.key}
			</Typography>
			<Typography sx={{ width: 40, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>{nf(row.styles)}</Typography>
			<Typography sx={{ width: 40, fontSize: 11.5, color: ink, textAlign: "right", flexShrink: 0 }}>{nf(row.options)}</Typography>
			<Typography sx={{ width: 40, fontSize: 11.5, color: muted, textAlign: "right", flexShrink: 0 }}>
				{row.contribution != null ? `${row.contribution}%` : "—"}
			</Typography>
			{canDrill ? <ChevronRightRounded sx={{ fontSize: 16, color: muted, flexShrink: 0 }} /> : <Box sx={{ width: 16, flexShrink: 0 }} />}
		</Stack>
	);
};

const ColorHeatMap = ({ rows }) => {
	const maxCell = useMemo(() => {
		let m = 0;
		for (const row of rows || []) {
			for (const band of STATUS_BANDS) {
				m = Math.max(m, Number(row[band.key]) || 0);
			}
		}
		return m || 1;
	}, [rows]);

	return (
		<Box>
			<Stack direction="row" alignItems="center" spacing={0.35} sx={{ pb: 0.5, position: "sticky", top: 0, bgcolor: "#fff", zIndex: 1 }}>
				<Typography sx={{ ...colHead, flex: 1, minWidth: 0 }}>Color</Typography>
				{STATUS_BANDS.map((band) => (
					<Typography
						key={band.key}
						sx={{ ...colHead, width: 28, textAlign: "center", flexShrink: 0, color: ink }}
						title={band.label}>
						{band.short}
					</Typography>
				))}
				<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>N</Typography>
				<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>%</Typography>
			</Stack>
			{(rows || []).map((row) => (
				<Stack
					key={row.key}
					direction="row"
					alignItems="center"
					spacing={0.35}
					sx={{ py: 0.35, borderTop: `1px solid ${line}` }}>
					<Typography sx={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: ink }} noWrap title={row.key}>
						{row.key}
					</Typography>
					{STATUS_BANDS.map((band) => {
						const n = Number(row[band.key]) || 0;
						return (
							<Tooltip key={band.key} arrow title={`${band.label}: ${nf(n)}`}>
								<Box
									sx={{
										width: 28,
										height: 22,
										flexShrink: 0,
										borderRadius: 0.75,
										display: "grid",
										placeItems: "center",
										background: heatFill(band.color, n / maxCell),
										border: `1px solid ${line}`,
										fontSize: 9.5,
										fontWeight: 600,
										color: ink,
										cursor: "default",
									}}>
									{n || "·"}
								</Box>
							</Tooltip>
						);
					})}
					<Typography sx={{ width: 36, fontSize: 11, color: ink, textAlign: "right", flexShrink: 0 }}>{nf(row.styles)}</Typography>
					<Typography sx={{ width: 36, fontSize: 11, color: muted, textAlign: "right", flexShrink: 0 }}>
						{row.contribution != null ? `${row.contribution}%` : "—"}
					</Typography>
				</Stack>
			))}
		</Box>
	);
};

/** Fashionability × Category heat — Published or All styles. */
const FashionabilityCategoryHeat = ({ payload, mode }) => {
	const categories = payload?.categories || [];
	const rows = payload?.rows || [];
	const publishedMode = mode === "published";
	const accent = publishedMode ? tone.published : tone.designerPending;

	const maxCell = useMemo(() => {
		let m = 0;
		for (const row of rows) {
			for (const cat of categories) {
				const cell = row.byCategory?.[cat];
				const n = Number(publishedMode ? cell?.published : cell?.all) || 0;
				m = Math.max(m, n);
			}
		}
		return m || 1;
	}, [rows, categories, publishedMode]);

	const colW = categories.length > 6 ? 34 : 40;

	if (!rows.length || !categories.length) {
		return (
			<Typography sx={{ fontSize: 12.5, color: muted, py: 2 }}>
				No fashionability × category data in scope.
			</Typography>
		);
	}

	return (
		<Box sx={{ minWidth: 0, overflowX: "auto" }}>
			<Stack
				direction="row"
				alignItems="center"
				spacing={0.35}
				sx={{ pb: 0.5, position: "sticky", top: 0, bgcolor: "#fff", zIndex: 1, minWidth: "max-content" }}>
				<Typography sx={{ ...colHead, width: 88, flexShrink: 0 }}>Fashionability</Typography>
				{categories.map((cat) => (
					<Typography
						key={cat}
						sx={{ ...colHead, width: colW, textAlign: "center", flexShrink: 0, color: ink, fontSize: 9 }}
						title={cat}
						noWrap>
						{cat.length > 6 ? `${cat.slice(0, 5)}…` : cat}
					</Typography>
				))}
				<Typography sx={{ ...colHead, width: 32, textAlign: "right", flexShrink: 0 }}>N</Typography>
			</Stack>
			{rows.map((row) => {
				const rowN = Number(publishedMode ? row.stylesPublished : row.stylesAll) || 0;
				return (
					<Stack
						key={row.key}
						direction="row"
						alignItems="center"
						spacing={0.35}
						sx={{ py: 0.35, borderTop: `1px solid ${line}`, minWidth: "max-content" }}>
						<Typography
							sx={{ width: 88, flexShrink: 0, fontSize: 11, fontWeight: 600, color: ink }}
							noWrap
							title={row.key}>
							{row.key}
						</Typography>
						{categories.map((cat) => {
							const cell = row.byCategory?.[cat];
							const n = Number(publishedMode ? cell?.published : cell?.all) || 0;
							return (
								<Tooltip key={cat} arrow title={`${row.key} · ${cat}: ${nf(n)}`}>
									<Box
										sx={{
											width: colW,
											height: 22,
											flexShrink: 0,
											borderRadius: 0.75,
											display: "grid",
											placeItems: "center",
											background: heatFill(accent, n / maxCell),
											border: `1px solid ${line}`,
											fontSize: 9.5,
											fontWeight: 600,
											color: ink,
											cursor: "default",
										}}>
										{n || "·"}
									</Box>
								</Tooltip>
							);
						})}
						<Typography sx={{ width: 32, fontSize: 11, color: ink, textAlign: "right", flexShrink: 0 }}>
							{nf(rowN)}
						</Typography>
					</Stack>
				);
			})}
		</Box>
	);
};

const StylesAnalytics = ({ filter = EMPTY_FILTER, search = "", onMoreFilters, activeFilterCount = 0 }) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(true);
	const [dimension, setDimension] = useState("brand");

	const [scopeOptions, setScopeOptions] = useState({ season: [], brand: [], division: [], category: [] });
	const scopeOptionsReady = useRef(false);

	/** Category-card-only filters — layered on top of More Filters. */
	const [catScope, setCatScope] = useState({ brand: [], division: [], season: [] });
	const [catPayload, setCatPayload] = useState(null);
	const [catLoading, setCatLoading] = useState(false);
	const [drillCategory, setDrillCategory] = useState(null);

	/**
	 * Shared filters for By color + Fashionability × Category:
	 * Brand / Category / Published|All / stage chips — all apply to both panes.
	 * Published ⇔ status [PUBLISHED]; All ⇔ no status filter.
	 */
	const [colorScope, setColorScope] = useState({ brand: [], category: [], status: ["PUBLISHED"] });
	const [colorPayload, setColorPayload] = useState(null); // { colors, fashionabilityCategory } when scoped
	const [colorLoading, setColorLoading] = useState(false);
	/** Category menu options scoped by selected Brand(s) on this shared card. */
	const [colorCategoryOptions, setColorCategoryOptions] = useState([]);
	/** Shared Published / All — kept in sync with status chips. */
	const [fashionMode, setFashionMode] = useState("published"); // "published" | "all"
	const colorBrandKey = useMemo(() => JSON.stringify(colorScope.brand || []), [colorScope.brand]);

	const effectiveFilter = useMemo(() => ({ ...(filter || EMPTY_FILTER) }), [filter]);
	const filterKey = useMemo(() => JSON.stringify(effectiveFilter || {}), [effectiveFilter]);
	const panelFilterEmpty = useMemo(() => {
		const keys = Object.keys(effectiveFilter || {}).filter((k) => {
			const v = effectiveFilter[k];
			if (v == null || v === "") return false;
			if (Array.isArray(v)) return v.length > 0;
			return true;
		});
		return keys.length === 0 && !String(search || "").trim();
	}, [effectiveFilter, search]);

	const isCatScoped = Boolean(catScope.brand.length || catScope.division.length || catScope.season.length);

	const catFilter = useMemo(() => {
		const next = { ...effectiveFilter };
		for (const key of ["brand", "division", "season"]) {
			if (catScope[key]?.length) next[key] = catScope[key];
		}
		return next;
	}, [effectiveFilter, catScope]);
	const catFilterKey = useMemo(() => JSON.stringify(catFilter || {}), [catFilter]);

	const isColorScoped = Boolean(
		colorScope.brand.length || colorScope.category.length || colorScope.status.length,
	);

	const colorFilter = useMemo(() => {
		const next = { ...effectiveFilter };
		for (const key of ["brand", "category", "status"]) {
			if (colorScope[key]?.length) next[key] = colorScope[key];
		}
		return next;
	}, [effectiveFilter, colorScope]);
	const colorFilterKey = useMemo(() => JSON.stringify(colorFilter || {}), [colorFilter]);

	const syncFashionModeFromStatus = (status) => {
		const publishedOnly = status.length === 1 && status[0] === "PUBLISHED";
		setFashionMode(publishedOnly ? "published" : "all");
	};

	/** Published / All — common to both heat maps (sets status filter for both). */
	const setPublishedAllMode = (mode) => {
		setFashionMode(mode);
		setColorScope((s) => ({
			...s,
			status: mode === "published" ? ["PUBLISHED"] : [],
		}));
	};

	const toggleColorStatus = (apiKey) => {
		setColorScope((s) => {
			const has = s.status.includes(apiKey);
			const status = has ? s.status.filter((k) => k !== apiKey) : [...s.status, apiKey];
			return { ...s, status };
		});
	};

	/** Keep Published/All highlight in sync when stage chips change. */
	useEffect(() => {
		syncFashionModeFromStatus(colorScope.status || []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(colorScope.status || [])]);

	const clearColorScope = () => {
		setColorScope({ brand: [], category: [], status: [] });
		setFashionMode("all");
	};

	const fetchSummary = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const { data: res } = await axios.post("/api/designer/styles-report/summary", {
				filter: effectiveFilter,
				search,
			});
			const result = res?.result || null;
			setData(result);
			if (result && panelFilterEmpty && !scopeOptionsReady.current) {
				scopeOptionsReady.current = true;
				setScopeOptions({
					season: result.filterOptions?.season || (result.groups?.season?.rows || []).map((r) => r.key),
					brand: result.filterOptions?.brand || (result.groups?.brand?.rows || []).map((r) => r.key),
					division: result.filterOptions?.division || [],
					category: result.filterOptions?.category || [],
				});
			}
		} catch (err) {
			setError(parseErrorMessage(err));
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterKey, search]);

	useEffect(() => {
		fetchSummary();
	}, [fetchSummary]);

	/** Reset drill when scope changes. */
	useEffect(() => {
		setDrillCategory(null);
	}, [catFilterKey, search]);

	/** Category table: panel data when card filters empty; else refetch with cat scope. */
	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			if (!isCatScoped) {
				setCatPayload(null);
				setCatLoading(false);
				return;
			}
			setCatLoading(true);
			try {
				const { data: res } = await axios.post("/api/designer/styles-report/summary", {
					filter: catFilter,
					search,
				});
				if (!cancelled) setCatPayload(res?.result?.categories || null);
			} catch (err) {
				if (!cancelled) {
					setCatPayload({ rows: [], truncated: false, error: parseErrorMessage(err) });
				}
			} finally {
				if (!cancelled) setCatLoading(false);
			}
		};
		run();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCatScoped, catFilterKey, search]);

	/** Color + fashionability: panel data when shared filters empty; else refetch both. */
	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			if (!isColorScoped) {
				setColorPayload(null);
				setColorLoading(false);
				return;
			}
			// Drop stale rows immediately so a prior all-brand / other-scope payload cannot linger.
			setColorPayload(null);
			setColorLoading(true);
			try {
				const { data: res } = await axios.post("/api/designer/styles-report/summary", {
					filter: colorFilter,
					search,
				});
				if (cancelled) return;
				setColorPayload({
					colors: res?.result?.colors || null,
					fashionabilityCategory: res?.result?.fashionabilityCategory || null,
				});
				// Brand-scoped responses also carry the Category menu for those brands.
				if (colorScope.brand.length) {
					const cats = res?.result?.filterOptions?.category || [];
					setColorCategoryOptions(cats);
					setColorScope((s) => {
						const pruned = (s.category || []).filter((c) => cats.includes(c));
						if (pruned.length === (s.category || []).length) return s;
						return { ...s, category: pruned };
					});
				}
			} catch (err) {
				if (!cancelled) {
					const msg = parseErrorMessage(err);
					setColorPayload({
						colors: { rows: [], truncated: false, error: msg },
						fashionabilityCategory: { rows: [], categories: [], error: msg },
					});
				}
			} finally {
				if (!cancelled) setColorLoading(false);
			}
		};
		run();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isColorScoped, colorFilterKey, search]);

	/**
	 * Category menu: all categories when no Brand on the shared card;
	 * when Brand is set, options come from the scoped fetch above.
	 */
	useEffect(() => {
		if (!colorScope.brand.length) {
			setColorCategoryOptions(scopeOptions.category || []);
		}
	}, [colorBrandKey, scopeOptions.category, colorScope.brand.length]);

	const { applied, ignored } = useMemo(() => splitAnalyticsFilters(effectiveFilter), [effectiveFilter]);

	const group = data?.groups?.[dimension];
	const rows = useMemo(() => group?.rows || [], [group]);

	const categories = isCatScoped ? catPayload : data?.categories;
	const categoryRows = useMemo(() => categories?.rows || [], [categories]);

	const drilledParent = useMemo(() => {
		if (!drillCategory) return null;
		return categoryRows.find((r) => r.key === drillCategory) || null;
	}, [categoryRows, drillCategory]);

	const visibleCategoryRows = drilledParent ? drilledParent.children || [] : categoryRows;

	const colors = isColorScoped ? colorPayload?.colors : data?.colors;
	const colorRows = useMemo(() => colors?.rows || [], [colors]);
	const fashionabilityCategory = isColorScoped
		? colorPayload?.fashionabilityCategory
		: data?.fashionabilityCategory;

	const colorScopeLabel = useMemo(() => {
		const bits = [];
		if (colorScope.brand.length) bits.push(colorScope.brand.join(", "));
		if (colorScope.category.length) bits.push(colorScope.category.join(", "));
		if (colorScope.status.length) {
			const labels = colorScope.status.map((api) => STATUS_BANDS.find((b) => b.api === api)?.short || api);
			bits.push(labels.join("+"));
		}
		return bits.join(" · ");
	}, [colorScope]);

	const tat = data?.timeToPublish;

	return (
		<Box sx={{ background: panelBg, border: `1px solid ${line}`, borderRadius: 3, p: 2.5, mb: 2 }}>
			<Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: 14.5, fontWeight: 600, color: ink, flexShrink: 0 }}>Style analytics</Typography>
				<Box sx={{ minWidth: 0, flex: "1 1 auto", overflow: "hidden" }}>
					<ScopeChips applied={applied} ignored={ignored} search={search} emptyLabel="all styles" />
				</Box>
				{typeof onMoreFilters === "function" ? (
					<Button
						size="small"
						variant="outlined"
						startIcon={<FilterAltRounded sx={{ fontSize: 16 }} />}
						onClick={onMoreFilters}
						sx={{
							textTransform: "none",
							fontSize: 12,
							color: muted,
							borderColor: activeFilterCount ? ink : line,
							bgcolor: activeFilterCount ? "#fff" : "transparent",
							borderRadius: 1,
							minHeight: 30,
							fontWeight: activeFilterCount ? 600 : 500,
							flexShrink: 0,
						}}>
						More Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
					</Button>
				) : null}
				{loading ? <CircularProgress size={15} sx={{ color: muted }} /> : null}
				<Tooltip title="Refresh" arrow>
					<span>
						<IconButton size="small" onClick={fetchSummary} disabled={loading}>
							<RefreshRounded sx={{ fontSize: 17, color: muted }} />
						</IconButton>
					</span>
				</Tooltip>
				<IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? "Collapse" : "Expand"}>
					{open ? (
						<ExpandLessRounded sx={{ fontSize: 19, color: muted }} />
					) : (
						<ExpandMoreRounded sx={{ fontSize: 19, color: muted }} />
					)}
				</IconButton>
			</Stack>

			<Collapse in={open}>
				{error ? (
					<Typography sx={{ fontSize: 13, color: "#B4696B", mt: 2 }}>{error}</Typography>
				) : !data && loading ? (
					<Stack alignItems="center" sx={{ py: 5 }}>
						<CircularProgress size={22} sx={{ color: tone.designerPending }} />
					</Stack>
				) : data ? (
					<Stack spacing={2} sx={{ mt: 2 }}>
						<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="stretch">
							{/* Left half — core pipeline KPIs; right half reserved for later tiles */}
							<Stack
								direction="row"
								spacing={1.5}
								flexWrap="wrap"
								useFlexGap
								sx={{
									width: { xs: "100%", md: "50%" },
									flexShrink: 0,
									boxSizing: "border-box",
									"& > *": { flex: "1 1 0", minWidth: 120 },
								}}>
								<KpiTile
									label="Total styles"
									value={nf(data.total)}
									hint="In current filter scope"
									info="COUNT(*) of styles in the active More Filters / search scope. Unscoped = full catalogue (all seasons). Attribute filters on style_details are not applied here — see the amber chip when any are ignored."
								/>
								<KpiTile
									label="Published"
									value={nf(data.published)}
									hint={`${data.publishedRate}% of total`}
									accent={tone.published}
									info="Published = status = 'PUBLISHED' (not published_date IS NOT NULL). published_date is sticky and overstates rate if you count by date. Formula: publishedRate = 100 × published / total."
								/>
								<KpiTile
									label="In progress"
									value={nf(data.pending)}
									hint={`${data.pendingRate}% of total`}
									accent={tone.sourcingPending}
									info="Pending = everything not PUBLISHED, null-safe: NOT (status <=> 'PUBLISHED'). Includes designer / sourcing / MRP pending and rejected. Formula: pendingRate = 100 × pending / total. Invariant: published + pending = total."
								/>
								<KpiTile
									label="Median to publish"
									value={days(tat?.medianDays)}
									hint={`Mean ${days(tat?.meanDays)} · n=${nf(tat?.sampleSize)}`}
									info="Time to publish = DATEDIFF(published_date, created_at) in days. Negative spans excluded. Median is preferred (mean is dragged by long tails). Sample n = styles with a usable span. Null/— when there is no sample — never shown as 0d."
								/>
							</Stack>
							<Stack
								direction="row"
								spacing={1.5}
								flexWrap="wrap"
								useFlexGap
								sx={{
									flex: 1,
									minWidth: 0,
									boxSizing: "border-box",
									"& > *": { flex: "1 1 0", minWidth: 140, maxWidth: 220 },
								}}>
								{/* Reserved — drop extra KPI cards here later */}
							</Stack>
						</Stack>

						<Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="stretch">
							{/* Compare by — ~⅓ */}
							<Box
								sx={{
									...card,
									width: { xs: "100%", md: "33.333%" },
									minWidth: { md: 260 },
									maxWidth: { md: 420 },
									height: COMPARE_H,
									flexShrink: 0,
									boxSizing: "border-box",
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									flexWrap="wrap"
									useFlexGap
									sx={{ flexShrink: 0 }}>
									<Typography sx={eyebrow}>Compare by</Typography>
									<DimensionToggle value={dimension} onChange={setDimension} options={DIMENSIONS} />
								</Stack>
								<Box sx={{ mt: 1, flexShrink: 0 }}>
									<Swatches items={STATUS_BANDS} />
								</Box>

								<Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.25, pb: 0.5, flexShrink: 0 }}>
									<Typography sx={{ ...colHead, flex: 1, minWidth: 0 }}>
										{dimension === "brand" ? "Brand" : "Season"}
									</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>N</Typography>
									<Typography sx={{ ...colHead, width: 72, textAlign: "center", flexShrink: 0 }}>Mix</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>Pub%</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>Med</Typography>
									<Typography sx={{ ...colHead, width: 36, textAlign: "right", flexShrink: 0 }}>30+</Typography>
								</Stack>

								<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
									{rows.length ? (
										rows.map((row) => <CompareRow key={row.key} row={row} />)
									) : (
										<Typography sx={{ fontSize: 12.5, color: muted, py: 2 }}>No styles to compare.</Typography>
									)}
								</Box>

								<Typography sx={{ fontSize: 10, color: muted, mt: 1, lineHeight: 1.4, flexShrink: 0 }}>
									Mix = status signal (hover). Med = days to publish. 30+ = unpublished older than 30 days.
									{group?.truncated ? (
										<Box component="span" sx={{ color: "#8A6A2F", fontWeight: 600 }}>
											{" "}
											Top {rows.length} only.
										</Box>
									) : null}
								</Typography>
							</Box>

							{/* Category / subcategory — ~⅓, fixed height + scroll */}
							<Box
								sx={{
									...card,
									width: { xs: "100%", md: "33.333%" },
									minWidth: { md: 260 },
									maxWidth: { md: 420 },
									height: COMPARE_H,
									flexShrink: 0,
									boxSizing: "border-box",
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={0.75}
									flexWrap="wrap"
									useFlexGap
									sx={{ flexShrink: 0 }}>
									{drilledParent ? (
										<>
											<IconButton
												size="small"
												onClick={() => setDrillCategory(null)}
												aria-label="Back to categories"
												sx={{ p: 0.25 }}>
												<ChevronLeftRounded sx={{ fontSize: 18, color: muted }} />
											</IconButton>
											<Typography sx={{ ...eyebrow, mr: 0.25 }} noWrap title={drilledParent.key}>
												{drilledParent.key}
											</Typography>
										</>
									) : (
										<Typography sx={{ ...eyebrow, mr: 0.25 }}>By category</Typography>
									)}
									<ScopeSelect
										label="Brand"
										options={scopeOptions.brand}
										value={catScope.brand}
										onChange={(v) => setCatScope((s) => ({ ...s, brand: v }))}
									/>
									<ScopeSelect
										label="Division"
										options={scopeOptions.division}
										value={catScope.division}
										onChange={(v) => setCatScope((s) => ({ ...s, division: v }))}
									/>
									<ScopeSelect
										label="Season"
										options={scopeOptions.season}
										value={catScope.season}
										onChange={(v) => setCatScope((s) => ({ ...s, season: v }))}
									/>
									{isCatScoped ? (
										<Button
											size="small"
											onClick={() => setCatScope({ brand: [], division: [], season: [] })}
											sx={{ textTransform: "none", fontSize: 11, color: muted, minWidth: 0, px: 0.5 }}>
											Clear
										</Button>
									) : null}
									{catLoading ? <CircularProgress size={12} sx={{ color: muted }} /> : null}
								</Stack>

								<Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1, pb: 0.5, flexShrink: 0 }}>
									<Typography sx={{ ...colHead, flex: 1, minWidth: 0 }}>
										{drilledParent ? "Sub-category" : "Category"}
									</Typography>
									<Typography sx={{ ...colHead, width: 40, textAlign: "right", flexShrink: 0 }}>Styles</Typography>
									<Typography sx={{ ...colHead, width: 40, textAlign: "right", flexShrink: 0 }}>Opts</Typography>
									<Typography sx={{ ...colHead, width: 40, textAlign: "right", flexShrink: 0 }}>%</Typography>
									<Box sx={{ width: 16, flexShrink: 0 }} />
								</Stack>

								<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
									{visibleCategoryRows.length ? (
										visibleCategoryRows.map((row) => (
											<CategoryRow
												key={row.key}
												row={row}
												onDrill={drilledParent ? undefined : (r) => setDrillCategory(r.key)}
											/>
										))
									) : (
										<Typography sx={{ fontSize: 12.5, color: categories?.error ? "#B4696B" : muted, py: 2 }}>
											{catLoading
												? "Loading…"
												: categories?.error
													? categories.error
													: !categories
														? "Category data unavailable — refresh after backend restart."
														: "No categories in scope."}
										</Typography>
									)}
								</Box>

								<Typography sx={{ fontSize: 10, color: muted, mt: 1, lineHeight: 1.4, flexShrink: 0 }}>
									% = share of styles in the parent slab (categories → 100%; sub-cats of one category → 100%). Opts =
									colour options.
									{categories?.truncated && !drilledParent ? (
										<Box component="span" sx={{ color: "#8A6A2F", fontWeight: 600 }}>
											{" "}
											Top {categoryRows.length} only.
										</Box>
									) : null}
								</Typography>
							</Box>

							{/* By color + Fashionability — shared Brand / Category / status filters */}
							<Box
								sx={{
									...card,
									flex: 1,
									minWidth: { xs: "100%", md: 420 },
									height: COMPARE_H,
									flexShrink: 1,
									boxSizing: "border-box",
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={0.75}
									flexWrap="wrap"
									useFlexGap
									sx={{ flexShrink: 0 }}>
									<Typography sx={{ ...eyebrow, mr: 0.25 }}>Colour & fashionability</Typography>
									<ScopeSelect
										label="Brand"
										options={scopeOptions.brand}
										value={colorScope.brand}
										onChange={(v) => setColorScope((s) => ({ ...s, brand: v, category: [] }))}
									/>
									<ScopeSelect
										label="Category"
										options={colorCategoryOptions}
										value={colorScope.category}
										onChange={(v) => setColorScope((s) => ({ ...s, category: v }))}
										searchable
										disabled={!colorCategoryOptions.length}
									/>
									{isColorScoped ? (
										<Button
											size="small"
											onClick={clearColorScope}
											sx={{ textTransform: "none", fontSize: 11, color: muted, minWidth: 0, px: 0.5 }}>
											Clear
										</Button>
									) : null}
									{colorLoading ? <CircularProgress size={12} sx={{ color: muted }} /> : null}
								</Stack>
								{colorScopeLabel ? (
									<Typography
										sx={{ fontSize: 10, color: muted, mt: 0.35, flexShrink: 0 }}
										noWrap
										title={colorScopeLabel}>
										Scoped: {colorScopeLabel}
									</Typography>
								) : null}

								<Stack
									direction="row"
									alignItems="center"
									spacing={0.5}
									flexWrap="wrap"
									useFlexGap
									sx={{ mt: 0.75, flexShrink: 0 }}>
									{[
										{ key: "published", label: "Published", color: tone.published },
										{ key: "all", label: "All", color: tone.designerPending },
									].map((opt) => {
										const on = fashionMode === opt.key;
										return (
											<Box
												key={opt.key}
												component="button"
												type="button"
												onClick={() => setPublishedAllMode(opt.key)}
												sx={{
													border: `1px solid ${on ? opt.color : line}`,
													background: on ? opt.color : "#fff",
													borderRadius: 1,
													px: 0.7,
													py: 0.2,
													fontSize: 10,
													fontWeight: on ? 600 : 500,
													color: ink,
													cursor: "pointer",
													lineHeight: 1.4,
												}}>
												{opt.label}
											</Box>
										);
									})}
									<Box sx={{ width: 1, height: 16, bgcolor: line, mx: 0.25, flexShrink: 0 }} />
									{STATUS_BANDS.map((band) => {
										const on = colorScope.status.includes(band.api);
										return (
											<Box
												key={band.api}
												component="button"
												type="button"
												onClick={() => toggleColorStatus(band.api)}
												sx={{
													border: `1px solid ${on ? band.color : line}`,
													background: on ? band.color : "#fff",
													borderRadius: 1,
													px: 0.7,
													py: 0.2,
													fontSize: 10,
													fontWeight: on ? 600 : 500,
													color: ink,
													cursor: "pointer",
													lineHeight: 1.4,
												}}>
												{band.short}
											</Box>
										);
									})}
								</Stack>

								<Stack
									direction={{ xs: "column", sm: "row" }}
									alignItems="stretch"
									spacing={1.5}
									sx={{ flex: 1, minHeight: 0, mt: 0.75 }}>
									{/* Left: Color × Status heat */}
									<Box
										sx={{
											flex: "1 1 0",
											minWidth: 0,
											display: "flex",
											flexDirection: "column",
											overflow: "hidden",
										}}>
										<Typography sx={{ ...eyebrow, flexShrink: 0 }}>By color</Typography>
										<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", mt: 0.5 }}>
											{colorRows.length ? (
												<ColorHeatMap rows={colorRows} />
											) : (
												<Typography sx={{ fontSize: 12.5, color: colors?.error ? "#B4696B" : muted, py: 2 }}>
													{colorLoading
														? "Loading…"
														: colors?.error
															? colors.error
															: !colors
																? "Color data unavailable — refresh after backend restart."
																: "No colours in scope."}
												</Typography>
											)}
										</Box>
										<Typography sx={{ fontSize: 10, color: muted, mt: 1, lineHeight: 1.4, flexShrink: 0 }}>
											Heat = styles per colour × stage. Published / All and stage chips apply to both panes. N / % =
											row total among colours shown.
											{colors?.truncated ? (
												<Box component="span" sx={{ color: "#8A6A2F", fontWeight: 600 }}>
													{" "}
													Top {colorRows.length} only.
												</Box>
											) : null}
										</Typography>
									</Box>

									{/* Right: Fashionability × Category cohort */}
									<Box
										sx={{
											flex: "1 1 0",
											minWidth: 0,
											display: "flex",
											flexDirection: "column",
											overflow: "hidden",
											borderLeft: { xs: "none", sm: `1px solid ${line}` },
											pl: { xs: 0, sm: 1.5 },
											borderTop: { xs: `1px solid ${line}`, sm: "none" },
											pt: { xs: 1, sm: 0 },
										}}>
										<Typography sx={{ ...eyebrow, flexShrink: 0 }}>Fashionability × Category</Typography>
										<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto", mt: 0.5 }}>
											{fashionabilityCategory?.rows?.length ? (
												<FashionabilityCategoryHeat payload={fashionabilityCategory} mode={fashionMode} />
											) : (
												<Typography
													sx={{
														fontSize: 12.5,
														color: fashionabilityCategory?.error ? "#B4696B" : muted,
														py: 2,
													}}>
													{colorLoading || loading
														? "Loading…"
														: fashionabilityCategory?.error
															? fashionabilityCategory.error
															: !fashionabilityCategory
																? "Fashionability data unavailable — refresh after backend restart."
																: "No fashionability × category data in scope."}
												</Typography>
											)}
										</Box>
										<Typography sx={{ fontSize: 10, color: muted, mt: 1, lineHeight: 1.4, flexShrink: 0 }}>
											Cells = styles per fashionability × category (
											{fashionMode === "published" ? "published only" : "all statuses"}
											). Same filters as colour.
											{fashionabilityCategory?.truncated ? (
												<Box component="span" sx={{ color: "#8A6A2F", fontWeight: 600 }}>
													{" "}
													Top cohorts only.
												</Box>
											) : null}
										</Typography>
									</Box>
								</Stack>
							</Box>
						</Stack>
					</Stack>
				) : null}
			</Collapse>
		</Box>
	);
};

export default StylesAnalytics;
